// Procedural food creatures. Spawn can roll a CreatureRecipe from a per-biome,
// hand-curated palette of part combinations instead of a fixed named species —
// ambient variety from the part set. Food/ambient tier only; showpieces stay
// hand-authored. The curation is the anti-"generic soup" guardrail.
//
// Each combo is a valid (body + ≤1 ornament) pairing with param ranges — the
// ≤1-ornament role budget is structural. Combos avoid the two-scalar clash (a
// count-using body is never paired with a count-using ornament; see
// recipeHasScalarClash), so every roll packs cleanly.

import type { BiomeId } from './biomes'
import type { CreatureRecipe, BodyPlanId, OrnamentId } from './creature-recipe'
import { weightedPick } from './weighted'

interface FoodCombo {
  weight: number
  body: BodyPlanId
  ornament?: OrnamentId
  count?: [number, number]   // sampled → recipe.count (ornament OR body consumer)
  length?: [number, number]  // sampled → recipe.length
}

// Shallow: calm classic microbes + the occasional darting rod.
const SHALLOW_FOOD: FoodCombo[] = [
  { weight: 6, body: 'blob' },
  { weight: 3, body: 'blob', ornament: 'cilia', count: [12, 20] },
  { weight: 5, body: 'blob', ornament: 'tendrils', count: [5, 9] },
  { weight: 6, body: 'grain' },
  { weight: 3, body: 'grain', ornament: 'cilia', count: [12, 18] },
  { weight: 4, body: 'grain', ornament: 'flagella' },
  { weight: 5, body: 'rod', length: [0.2, 0.4] },
  { weight: 2, body: 'rod', ornament: 'cilia', count: [8, 14] },
  { weight: 3, body: 'blob', ornament: 'dnaStrand' },
]

// Deep: cooler, stranger — spirilla, dna strands, the odd bristled cell.
const DEEP_FOOD: FoodCombo[] = [
  { weight: 5, body: 'blob' },
  { weight: 4, body: 'blob', ornament: 'tendrils', count: [6, 11] },
  { weight: 4, body: 'blob', ornament: 'dnaStrand' },
  { weight: 5, body: 'grain' },
  { weight: 3, body: 'grain', ornament: 'cilia', count: [10, 16] },
  { weight: 5, body: 'spiral', count: [2, 4] },
  { weight: 3, body: 'rod', length: [0.25, 0.45] },
  { weight: 2, body: 'grain', ornament: 'dnaStrand' },
]

// Mushroom floor (tier 3): earthy — grains, dna strands, the odd spiral/rod.
// (Mushroom-bodied food is added with the mushroom creature set.)
const MUSHROOM_FOOD: FoodCombo[] = [
  { weight: 7, body: 'mushroom', ornament: 'gills' },
  { weight: 3, body: 'mushroom' },
  { weight: 5, body: 'grain' },
  { weight: 3, body: 'grain', ornament: 'dnaStrand' },
  { weight: 4, body: 'blob' },
  { weight: 3, body: 'rod', length: [0.25, 0.45] },
  { weight: 3, body: 'spiral', count: [2, 3] },
]

const PALETTES: Record<BiomeId, FoodCombo[]> = {
  shallow: SHALLOW_FOOD,
  deep: DEEP_FOOD,
  mushroom: MUSHROOM_FOOD,
}

function sample(range: [number, number]): number {
  return range[0] + Math.random() * (range[1] - range[0])
}

/** Roll a procedural food-creature recipe for a biome. */
export function rollFoodRecipe(biome: BiomeId): CreatureRecipe {
  const combo = weightedPick(PALETTES[biome], c => c.weight)
  const recipe: CreatureRecipe = { body: combo.body }
  if (combo.ornament) recipe.ornaments = [combo.ornament]
  if (combo.count) recipe.count = Math.round(sample(combo.count))
  if (combo.length) recipe.length = sample(combo.length)
  return recipe
}
